import type {
  TaxYearInput,
  TaxResult,
  BracketResult,
  TipItem,
} from "../types/tax.types";
import { DeductionKey } from "../types/tax.types";

const BRACKETS: Array<{ limit: number; rate: number }> = [
  { limit: 150_000, rate: 0 },
  { limit: 300_000, rate: 0.05 },
  { limit: 500_000, rate: 0.1 },
  { limit: 750_000, rate: 0.15 },
  { limit: 1_000_000, rate: 0.2 },
  { limit: 2_000_000, rate: 0.25 },
  { limit: 5_000_000, rate: 0.3 },
  { limit: Infinity, rate: 0.35 },
];

function calcBrackets(netTaxable: number): BracketResult[] {
  const results: BracketResult[] = [];
  let remaining = netTaxable;
  let prevLimit = 0;

  for (const bracket of BRACKETS) {
    const width =
      bracket.limit === Infinity ? remaining : bracket.limit - prevLimit;
    const taxableInBracket = Math.min(remaining, width);
    results.push({
      from: prevLimit,
      to: bracket.limit === Infinity ? null : bracket.limit,
      rate: bracket.rate,
      taxableInBracket,
      taxInBracket: Math.round(taxableInBracket * bracket.rate),
    });
    remaining -= taxableInBracket;
    prevLimit = bracket.limit === Infinity ? prevLimit : bracket.limit;
    if (remaining <= 0) break;
  }

  return results;
}

function calcTips(
  input: TaxYearInput,
  result: TaxResult,
  ceYear: number,
  enabled?: ReadonlyArray<DeductionKey>,
): TipItem[] {
  const isEnabled = (...keys: DeductionKey[]) =>
    !enabled || keys.some((k) => enabled.includes(k));
  const tips: TipItem[] = [];
  const totalIncome = result.totalGrossIncome;
  const marginalRate =
    result.brackets.findLast((b) => b.taxableInBracket > 0)?.rate ?? 0;

  // Retirement room (RMF + SSF + PVD combined cap 500k)
  const retirementRoom = 500_000 - result.retirementCombined;
  if (
    isEnabled(DeductionKey.Pvd, DeductionKey.Rmf, DeductionKey.Ssf) &&
    retirementRoom > 0
  ) {
    const saving = Math.round(retirementRoom * marginalRate);
    tips.push({
      id: "retirement",
      title: "Boost retirement savings",
      description: `฿${retirementRoom.toLocaleString()} of RMF/SSF/PVD room remaining (combined cap ฿500,000)`,
      potentialSaving: saving,
      priority: saving > 5_000 ? "high" : saving > 1_000 ? "medium" : "low",
    });
  }

  // TESG room
  const tesgCap = Math.min(totalIncome * 0.3, 300_000);
  const tesgRoom = tesgCap - result.tesgDeduction;
  if (isEnabled(DeductionKey.Tesg) && tesgRoom > 1_000) {
    const saving = Math.round(tesgRoom * marginalRate);
    tips.push({
      id: "tesg",
      title: "Invest in TESG (Thai ESG Fund)",
      description: `฿${Math.round(tesgRoom).toLocaleString()} of TESG room remaining (separate cap ฿300,000)`,
      potentialSaving: saving,
      priority: saving > 5_000 ? "high" : "medium",
    });
  }

  // Life/health insurance room
  const insuranceCap = 100_000;
  const insuranceUsed =
    result.lifeInsuranceDeduction + result.healthInsuranceDeduction;
  const insuranceRoom = insuranceCap - insuranceUsed;
  if (
    isEnabled(DeductionKey.LifeInsurance, DeductionKey.HealthInsurance) &&
    insuranceRoom > 0
  ) {
    const saving = Math.round(insuranceRoom * marginalRate);
    tips.push({
      id: "insurance",
      title: "Life / health insurance",
      description: `฿${insuranceRoom.toLocaleString()} of life/health insurance room remaining (cap ฿100,000)`,
      potentialSaving: saving,
      priority: saving > 5_000 ? "high" : "medium",
    });
  }

  // e-Receipt (2025 only)
  if (ceYear === 2025 && isEnabled(DeductionKey.EasyEReceipt)) {
    const eReceiptCap = 50_000;
    const eReceiptUsed = input.easyEReceipt;
    const eReceiptRoom = eReceiptCap - eReceiptUsed;
    if (eReceiptRoom > 0) {
      const saving = Math.round(
        Math.min(eReceiptRoom, eReceiptCap) * 2 * marginalRate,
      );
      tips.push({
        id: "ereceipt",
        title: "Easy e-Receipt 2025",
        description: `Spend ฿${eReceiptRoom.toLocaleString()} more via e-Receipt to reach the ฿100,000 deduction cap`,
        potentialSaving: saving,
        priority: saving > 5_000 ? "high" : "medium",
      });
    }
  }

  // Home loan interest room
  const homeLoanRoom = 100_000 - result.homeLoanInterestDeduction;
  if (
    isEnabled(DeductionKey.HomeLoanInterest) &&
    homeLoanRoom > 0 &&
    input.homeLoanInterest > 0
  ) {
    const saving = Math.round(homeLoanRoom * marginalRate);
    tips.push({
      id: "homeloan",
      title: "Home loan interest",
      description: `฿${homeLoanRoom.toLocaleString()} of home loan interest deduction room remaining (cap ฿100,000)`,
      potentialSaving: saving,
      priority: "low",
    });
  }

  return tips
    .filter((t) => t.potentialSaving > 0)
    .sort((a, b) => b.potentialSaving - a.potentialSaving)
    .slice(0, 5);
}

export function calculateTax(
  input: TaxYearInput,
  ceYear: number,
  enabledDeductions?: ReadonlyArray<DeductionKey>,
): TaxResult {
  const totalGrossIncome = input.employmentIncome + input.freelanceRevenue;

  // Expense deductions
  const employmentExpenseDeduction = Math.min(
    input.employmentIncome * 0.5,
    100_000,
  );
  const freelanceExpenseDeduction =
    input.freelanceMethod === "standard"
      ? input.freelanceRevenue * 0.6
      : Math.max(0, input.freelanceActualExpenses);
  const totalExpenseDeduction =
    employmentExpenseDeduction + freelanceExpenseDeduction;
  const incomeAfterExpense = Math.max(
    0,
    totalGrossIncome - totalExpenseDeduction,
  );

  // Personal & family
  const personalAllowance = 60_000;
  const spouseAllowance = input.hasSpouse ? 60_000 : 0;
  const childrenAllowance = input.childrenCount * 30_000;
  const parentSupportAllowance = Math.min(input.parentSupportCount, 4) * 30_000;
  const disabledSupportAllowance = input.disabledSupportCount * 60_000;

  // Insurance
  const healthInsuranceDeduction = Math.min(input.healthInsurance, 25_000);
  const lifeInsuranceDeduction = Math.min(
    input.lifeInsurance,
    100_000 - healthInsuranceDeduction,
  );
  const parentHealthInsuranceDeduction = Math.min(
    input.parentHealthInsurance,
    15_000,
  );
  const ssoDeduction = Math.min(input.sso, 9_000);

  // Retirement (combined cap 500k for pvd+rmf+ssf)
  const pvdDeductionRaw = Math.min(input.pvd, totalGrossIncome * 0.15, 500_000);
  const rmfDeductionRaw = Math.min(input.rmf, totalGrossIncome * 0.3, 500_000);
  const ssfDeductionRaw = Math.min(input.ssf, totalGrossIncome * 0.3, 200_000);
  const retirementCombined = Math.min(
    pvdDeductionRaw + rmfDeductionRaw + ssfDeductionRaw,
    500_000,
  );
  // Distribute combined cap proportionally
  const retirementRaw = pvdDeductionRaw + rmfDeductionRaw + ssfDeductionRaw;
  const retirementScale =
    retirementRaw > 0 ? retirementCombined / retirementRaw : 0;
  const pvdDeduction = Math.round(pvdDeductionRaw * retirementScale);
  const rmfDeduction = Math.round(rmfDeductionRaw * retirementScale);
  const ssfDeduction = Math.round(ssfDeductionRaw * retirementScale);
  const tesgDeduction = Math.min(input.tesg, totalGrossIncome * 0.3, 300_000);

  // Housing
  const homeLoanInterestDeduction = Math.min(input.homeLoanInterest, 100_000);
  const newHomeEReceiptDeduction =
    ceYear === 2025 ? Math.min(input.newHomeEReceipt, 100_000) : 0;

  // Pre-donation net (needed for education donation cap)
  const predonationDeductions =
    personalAllowance +
    spouseAllowance +
    childrenAllowance +
    parentSupportAllowance +
    disabledSupportAllowance +
    lifeInsuranceDeduction +
    healthInsuranceDeduction +
    parentHealthInsuranceDeduction +
    ssoDeduction +
    retirementCombined +
    tesgDeduction +
    homeLoanInterestDeduction +
    newHomeEReceiptDeduction;
  const predonationNet = Math.max(
    0,
    incomeAfterExpense - predonationDeductions,
  );

  // Donations
  const easyEReceiptDeduction =
    ceYear === 2025 ? Math.min(input.easyEReceipt * 2, 100_000) : 0;
  const educationDonationDeduction = Math.min(
    input.educationDonation * 2,
    predonationNet * 0.1,
  );
  const politicalDonationDeduction = Math.min(input.politicalDonation, 10_000);

  const totalDeductions =
    totalExpenseDeduction +
    predonationDeductions +
    easyEReceiptDeduction +
    educationDonationDeduction +
    politicalDonationDeduction;

  const netTaxableIncome = Math.max(
    0,
    predonationNet -
      easyEReceiptDeduction -
      educationDonationDeduction -
      politicalDonationDeduction,
  );

  const brackets = calcBrackets(netTaxableIncome);
  const grossTax = brackets.reduce((sum, b) => sum + b.taxInBracket, 0);
  const withholdingCredit = input.withholdingTax;
  const netTax = Math.max(0, grossTax - withholdingCredit);
  const refund = Math.max(0, withholdingCredit - grossTax);
  const effectiveRate = totalGrossIncome > 0 ? grossTax / totalGrossIncome : 0;

  const partialResult: TaxResult = {
    totalGrossIncome,
    employmentExpenseDeduction,
    freelanceExpenseDeduction,
    totalExpenseDeduction,
    incomeAfterExpense,
    personalAllowance,
    spouseAllowance,
    childrenAllowance,
    parentSupportAllowance,
    disabledSupportAllowance,
    lifeInsuranceDeduction,
    healthInsuranceDeduction,
    parentHealthInsuranceDeduction,
    ssoDeduction,
    pvdDeduction,
    rmfDeduction,
    ssfDeduction,
    retirementCombined,
    tesgDeduction,
    homeLoanInterestDeduction,
    newHomeEReceiptDeduction,
    easyEReceiptDeduction,
    educationDonationDeduction,
    politicalDonationDeduction,
    totalDeductions,
    netTaxableIncome,
    brackets,
    grossTax,
    withholdingCredit,
    netTax,
    refund,
    effectiveRate,
    tips: [],
  };

  return {
    ...partialResult,
    tips: calcTips(input, partialResult, ceYear, enabledDeductions),
  };
}
